import { Capacitor } from "@capacitor/core";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";

export const isNativeMobileApp = () => {
  const platform = Capacitor.getPlatform();

  return (
    Capacitor.isNativePlatform() &&
    (platform === "android" || platform === "ios")
  );
};

const mapSource = (source) => {
  if (source === "camera") return CameraSource.Camera;
  if (source === "gallery") return CameraSource.Photos;

  return CameraSource.Prompt;
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;

    reader.readAsDataURL(blob);
  });

const compressDataUrlToFile = async (dataUrl, filename = "image.jpg") => {
  const img = new Image();

  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = dataUrl;
  });

  const maxSize = 1400;
  let { width, height } = img;

  if (width > height && width > maxSize) {
    height = Math.round((height * maxSize) / width);
    width = maxSize;
  } else if (height > width && height > maxSize) {
    width = Math.round((width * maxSize) / height);
    height = maxSize;
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.75)
  );

  if (!blob) {
    throw new Error("Bild konnte nicht komprimiert werden.");
  }

  return new File([blob], filename.replace(/\.[^.]+$/, ".jpg"), {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
};

const createNativeImageEntry = async (photo, filename) => {
  const webPath = photo?.webPath;
  const nativePath = photo?.path || webPath;

  if (!webPath) return null;

  const res = await fetch(webPath);
  const blob = await res.blob();
  const dataUrl = await blobToDataUrl(blob);

  const file = await compressDataUrlToFile(dataUrl, filename);

  file.nativePath = nativePath;

  return {
    file,
    preview: dataUrl,
    uri: nativePath,
    nativePath,
    type: "new",
  };
};

export const makeNativeImageEntry = async (
  prefix = "image",
  source = "prompt"
) => {
  if (!isNativeMobileApp()) return null;

  const image = await Camera.getPhoto({
    quality: 75,
    allowEditing: false,
    resultType: CameraResultType.Uri,
    source: mapSource(source),
  });

  return await createNativeImageEntry(
    image,
    `${prefix}-${Date.now()}.jpg`
  );
};

export const makeNativeImageEntries = async ({
  prefix = "image",
  source = "gallery",
  limit = 5,
} = {}) => {
  if (!isNativeMobileApp()) return [];

  if (source === "camera") {
    const entry = await makeNativeImageEntry(prefix, "camera");
    return entry ? [entry] : [];
  }

  const result = await Camera.pickImages({
    quality: 75,
    limit,
  });

  const photos = Array.isArray(result?.photos)
    ? result.photos.slice(0, limit)
    : [];

  const timestamp = Date.now();

  const entries = await Promise.all(
    photos.map((photo, index) =>
      createNativeImageEntry(
        photo,
        `${prefix}-${timestamp}-${index}.jpg`
      )
    )
  );

  return entries.filter(Boolean);
};

export const pickSingleImageNative = async (source = "prompt") => {
  if (!isNativeMobileApp()) return null;

  const image = await Camera.getPhoto({
    quality: 75,
    allowEditing: false,
    resultType: CameraResultType.Uri,
    source: mapSource(source),
  });

  if (!image?.webPath) return null;

  return {
    dataUrl: image.webPath,
    uri: image.path || image.webPath,
    nativePath: image.path || image.webPath,
    format: image.format || "jpeg",
  };
};